import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";
import { ChartBar, MagnifyingGlass, X } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import AuditFilterChips from "@/components/audit/AuditFilterChips";
import AuditTable from "@/components/audit/AuditTable";

const DEFAULT_FILTERS = {
  action: "__any__",
  user_email: "",
  target: "",
  date_from: "",
  date_to: "",
  limit: 200,
};

export default function Audit() {
  const [items, setItems] = useState([]);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const location = useLocation();

  const load = useCallback(async (nextFilters) => {
    setLoading(true);
    try {
      const active = nextFilters || filters;
      const params = {};
      if (active.action && active.action !== "__any__") params.action = active.action;
      if (active.user_email) params.user_email = active.user_email;
      if (active.target) params.target = active.target;
      if (active.date_from) params.date_from = active.date_from;
      if (active.date_to) params.date_to = active.date_to;
      params.limit = active.limit || 200;
      const { data } = await api.get("/audit", { params });
      setItems(data);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    api.get("/audit/actions").then((r) => setActions(r.data)).catch(() => setActions([]));
    // Pre-apply filter from ?action=... in the URL (used by dashboard widgets)
    const qs = new URLSearchParams(location.search);
    const preAction = qs.get("action");
    if (preAction) {
      const next = { ...DEFAULT_FILTERS, action: preAction };
      setFilters(next);
      load(next);
    } else {
      load(DEFAULT_FILTERS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const setF = (patch) => setFilters((f) => ({ ...f, ...patch }));

  const clearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    load(DEFAULT_FILTERS);
  };

  const onQuickFilter = (action) => {
    const next = { ...filters, action };
    setFilters(next);
    load(next);
  };

  const actionOptions = useMemo(() => {
    const prefixes = new Set(actions.map((a) => a.split(".")[0]).filter(Boolean));
    return { prefixes: [...prefixes].sort(), exact: actions };
  }, [actions]);

  const hasActiveFilter =
    filters.action !== "__any__" ||
    filters.user_email ||
    filters.target ||
    filters.date_from ||
    filters.date_to;

  return (
    <div className="p-6 md:p-8 max-w-6xl">
      <div className="border-b border-border pb-6 mb-6">
        <div className="overline">Sistem</div>
        <h1 className="text-3xl md:text-4xl font-black tracking-tight mt-2 flex items-center gap-3">
          <span className="w-11 h-11 rounded-full bg-orange-100 text-primary flex items-center justify-center shrink-0">
            <ChartBar size={22} weight="bold" />
          </span>
          Audit Trail
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Semua aktivitas penting sistem. Gunakan quick-filter atau filter lanjut di bawah untuk mempersempit hasil.
        </p>
      </div>

      <AuditFilterChips activeAction={filters.action} onSelect={onQuickFilter} />

      {/* Advanced filter bar */}
      <div className="bg-card border border-border mb-4 p-4 grid grid-cols-1 md:grid-cols-6 gap-3">
        <div>
          <Label className="overline block mb-1">Aksi</Label>
          <Select value={filters.action} onValueChange={(v) => setF({ action: v })}>
            <SelectTrigger className="rounded-xl h-9" data-testid="audit-action-select">
              <SelectValue placeholder="Semua" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__any__">Semua</SelectItem>
              {actionOptions.prefixes.length > 0 && (
                <>
                  <SelectItem value="__prefix_header__" disabled>Kelompok</SelectItem>
                  {actionOptions.prefixes.map((p) => (
                    <SelectItem key={`prefix:${p}`} value={`prefix:${p}`}>{p}.*</SelectItem>
                  ))}
                </>
              )}
              {actionOptions.exact.length > 0 && (
                <>
                  <SelectItem value="__exact_header__" disabled>Tepat</SelectItem>
                  {actionOptions.exact.map((a) => (
                    <SelectItem key={`exact:${a}`} value={a}>{a}</SelectItem>
                  ))}
                </>
              )}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="overline block mb-1">User email</Label>
          <Input value={filters.user_email} onChange={(e) => setF({ user_email: e.target.value })}
                 className="rounded-xl h-9" placeholder="admin@spb.local" data-testid="audit-user-input" />
        </div>
        <div>
          <Label className="overline block mb-1">Target ID</Label>
          <Input value={filters.target} onChange={(e) => setF({ target: e.target.value })}
                 className="rounded-xl h-9" placeholder="id" data-testid="audit-target-input" />
        </div>
        <div>
          <Label className="overline block mb-1">Dari tanggal</Label>
          <Input type="date" value={filters.date_from} onChange={(e) => setF({ date_from: e.target.value })}
                 className="rounded-xl h-9" data-testid="audit-date-from" />
        </div>
        <div>
          <Label className="overline block mb-1">Sampai tanggal</Label>
          <Input type="date" value={filters.date_to} onChange={(e) => setF({ date_to: e.target.value })}
                 className="rounded-xl h-9" data-testid="audit-date-to" />
        </div>
        <div className="flex items-end gap-2">
          <Button onClick={() => load()} className="rounded-xl flex-1" data-testid="audit-apply-btn">
            <MagnifyingGlass size={14} weight="bold" /> Terapkan
          </Button>
          {hasActiveFilter && (
            <Button onClick={clearFilters} variant="outline" className="rounded-xl" title="Reset filter"
                    data-testid="audit-clear-btn">
              <X size={14} weight="bold" />
            </Button>
          )}
        </div>
      </div>

      <AuditTable items={items} loading={loading} />
    </div>
  );
}
